import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/site/Reveal";

const steps = [
  {
    n: "01",
    title: "Planning & Feasibility",
    body:
      "Site analysis, budget shaping, consultant coordination. We help you understand what's possible before you invest.",
    detail: [
      "Site & context analysis",
      "Preliminary budget shaping",
      "Consultant coordination",
      "Cost planning against scheme options",
    ],
  },
  {
    n: "02",
    title: "Construction",
    body:
      "A committed team, long-standing trades, weekly reporting. Programs run tight; quality runs deep.",
    detail: [
      "Trades procured and coordinated",
      "Program managed weekly",
      "Structured client reporting",
      "Design intent held through delivery",
    ],
  },
  {
    n: "03",
    title: "Completion & Handover",
    body:
      "A structured walk-through process for handover, documentation, and a builder who stays reachable.",
    detail: [
      "Practical completion",
      "Defect close-out ahead of handover",
      "Warranty & documentation package",
      "Ongoing aftercare",
    ],
  },
];

export default function ProcessPage() {
  return (
    <div data-testid="process-page" className="bg-zinc-950">
      <section className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">
              — How we work
            </div>
            <h1 className="mt-6 font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-zinc-100 leading-[1.02] max-w-5xl">
              Three stages. <span className="italic text-zinc-300">One accountable partner.</span>
            </h1>
            <p className="mt-8 text-zinc-400 text-lg max-w-2xl leading-relaxed">
              We keep the process simple — close collaboration and consistent
              attention from first sketch to final walk-through.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4" />
          <div className="md:col-span-8 relative">
            <div className="absolute left-0 md:left-4 top-2 bottom-2 w-px bg-zinc-800" />
            <ol className="space-y-20 pl-8 md:pl-16">
              {steps.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.08}>
                  <li className="relative">
                    <span className="absolute -left-8 md:-left-16 top-3 w-2 h-2 bg-zinc-100 rounded-full" />
                    <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">
                      Stage {s.n}
                    </div>
                    <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-zinc-100 tracking-tight">
                      {s.title}
                    </h2>
                    <p className="mt-4 text-zinc-400 text-lg leading-relaxed max-w-xl">
                      {s.body}
                    </p>
                    <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                      {s.detail.map((d, j) => (
                        <li
                          key={j}
                          className="flex items-start gap-3 text-zinc-500 text-sm"
                        >
                          <span className="mt-2 w-1 h-1 bg-zinc-600" />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="border-t border-zinc-900 py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tighter text-zinc-100 leading-[1.05]">
              Ready to walk through your project?
            </h2>
            <Link
              to="/contact"
              data-testid="process-cta-contact"
              className="inline-flex items-center gap-3 mt-10 bg-zinc-100 text-zinc-950 px-8 py-4 text-[12px] tracking-[0.25em] uppercase hover:bg-white transition"
            >
              Start a Conversation
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
